/**
 * Contact-type classification for connections.
 *
 * Single source of truth for whether a connection's avatar shows the Royale ring.
 * Read from BOTH the People list card and the contact detail page so the two never
 * diverge — they used to: the card dropped the ring for deleted accounts while the
 * detail page kept it.
 */

import { isPeerGone } from './peerGone';

/**
 * Is this connection a FULL Royale user? (The ring = permanent, logged-in
 * accounts ONLY — user ruling 2026-08-08; a Simple profile is a temp identity
 * and never rings.)
 *
 * True only when the peer has a profile account (senderProfileUserId) that has
 * not gone — neither account-deleted nor left this connection (isPeerGone,
 * CD #15) AND the list enrichment has stamped it FULL (peerAccountStatus, from
 * get_connection_profiles — migration 028). Legacy anonData without the stamp
 * reads as not-Full until the next list enrichment heals it (one People load).
 *
 * @param {Object} [anonData] - The connection's anonData (the peer's shared data)
 * @returns {boolean}
 */
export const isRoyaleUser = (anonData) =>
  !!anonData?.senderProfileUserId &&
  !isPeerGone(anonData) &&
  anonData?.peerAccountStatus === 'full';

/**
 * Sender-side sibling of isRoyaleUser for the ANON-received card
 * (`senderAccountStatus` rides the host's payload, not enrichment). The
 * default INVERTS deliberately: a MISSING marker means a legacy host, and
 * every legacy host was Full — old stored cards keep their ring. One home
 * for the rule (review 2026-08-09: two inline copies with the subtle
 * missing-means-Full clause were a flip-it-wrong hazard).
 *
 * @param {Object} [userData] - The received card data (profileData)
 * @returns {boolean}
 */
export const isFullSender = (userData) => userData?.senderAccountStatus !== 'anon';
